import { Key } from 'react'
import { Item, Section } from '@react-stately/collections'
import { ItemElement } from '@react-types/shared'
import cx from 'classnames'

import Styles from '../../ProfileDropdown.module.scss'

import { useFeatureFlagUserGroup } from 'hooks/useFeatureFlagUserGroup'
import { useCanViewAs } from 'components/Can'
import { useAuth } from 'hooks/UseAuth'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

import { USERS, checkFeatureFlagUserGroup } from 'utils/featureFlagUserGroups'

import { getInitials } from 'utils'

import Avatar from 'common/Avatar/Avatar'
import { Menu } from 'components/Menu/Menu'
import { Flag } from 'almanac-ui/icons'
import { ChevronRight } from 'blaze-ui/icons/24'
import {
  Card,
  UsersProfiles1,
  Logout1,
  Gift01,
  HelpCenterIcon,
  SupportBubble,
  BugIcon,
  Users,
  ChevronRightSmall,
  ArrowRefresh,
} from 'blaze-ui/icons'
import { Settings } from 'blaze-ui/icons/20/Settings'
import { Workspace, UserRole, SettingsMember } from 'types'
import { WithAutopilotUpgradeBadge } from 'components/AutopilotUpgradeButtons/WithAutopilotUpgradeBadge'
import { useAPMobileWeb } from 'hooks/useAPMobileWeb'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { useRootStore } from 'stores/RootStore'
import { CreditUsage as CreditUsageProp } from 'api/clients/CreditUsageClient'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { REACT_APP_BETA_ENVIRONMENT } from 'env'

const CreditUsage = ({ usage }: { usage: CreditUsageProp }) => {
  const percentUsage = ((usage?.total_credits_used ?? 0) / (usage?.total_credits_available ?? 1)) * 100

  return (
    <div className={Styles.creditUsageContainer}>
      <div className={Styles.top}>
        <Text variant="label" color="var(--dark-80)">
          Credits
        </Text>
        <div className={Styles.right}>
          <Text variant="metadata" color="var(--dark-60)">
            <strong>{usage?.credits_remaining}</strong> left
          </Text>
          <ChevronRightSmall size={16} />
        </div>
      </div>
      <div className={Styles.bottom}>
        <div className={Styles.progressBar}>
          <div
            className={cx(Styles.creditsUsed, { [Styles.oneHundredPercent]: percentUsage === 100 })}
            style={{
              width: `${percentUsage}%`,
            }}
          />
        </div>
        <div className={Styles.chartKey}>
          <div className={Styles.used}>
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 5 5" fill="none">
              <circle cx="2.5" cy="2.5" r="2.5" fill="#A7B1B8" />
            </svg>
            <Text variant="metadata" color="var(--dark-60)">
              Used
            </Text>
          </div>
          <div className={Styles.left}>
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 5 5" fill="none">
              <circle cx="2.5" cy="2.5" r="2.5" fill="var(--selected-fill)" />
            </svg>
            <Text variant="metadata" color="var(--dark-60)">
              Left
            </Text>
          </div>
        </div>
      </div>
    </div>
  )
}

export const MainMenu = ({
  onAction,
  workspace,
  profile,
}: {
  onAction: (key: Key) => void
  workspace: Workspace
  profile?: SettingsMember
}) => {
  // Not using the useIsSystemRedesign hook because it would cause lots of tests that are not wrapped in redux Providers and that render menus to fail
  const isSystemRedesignEnabled = document.documentElement.getAttribute('data-autopilot-system-redesign') === 'on'
  const canOverrideFlipper = useFeatureFlagUserGroup([USERS.withAlmanacEmail])
  const canViewAs = useCanViewAs(workspace.id)
  const auth = useAuth()
  const isAlmanacUser = checkFeatureFlagUserGroup([USERS.withAlmanacEmail], auth?.email)
  const { isTabletPortrait } = useResponsiveDesign()

  const isAPMobileWeb = useAPMobileWeb()
  const showReleaseInfo = useFlipperFlag('show_release_info')

  const desktopIconSize = isSystemRedesignEnabled ? 16 : undefined
  const iconSize = isAPMobileWeb ? 24 : desktopIconSize
  const avatarBgColor = isSystemRedesignEnabled ? '#dfdfdf' : 'var(--avatar)'
  const { creditUsage } = useRootStore().creditUsageStore
  const showCreditUsage = workspace.is_virtual_marketer && creditUsage && Object.keys(creditUsage).length > 0

  return (
    <div>
      <Menu
        aria-label="Workspaces"
        className={isAPMobileWeb ? Styles.newMobileWebMenu : undefined}
        itemClassName={cx(Styles.menuItem, { [Styles.newMobileWeb]: isAPMobileWeb })}
        onAction={onAction}
        selectionMode="single"
        selectedKeys={new Set([`workspace-${workspace.id}`])}
        showCheckmark={false}
      >
        <Section>
          {
            [
              canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]) && (
                <Item key="profile" textValue="Profile">
                  <div className={cx(Styles.selectedWorkspace, { [Styles.newMobileWeb]: isAPMobileWeb })}>
                    {profile?.avatar_url ? (
                      <div className={Styles.profileButtonContainer}>
                        <img className={Styles.profileButton} src={profile.avatar_url} alt="user profile" />
                        <Avatar
                          className={Styles.avatar}
                          label={getInitials(workspace.name)}
                          size={isAPMobileWeb ? 47 : 20}
                          workspaceId={workspace.id}
                          url={workspace.logo_url}
                          backgroundColor={avatarBgColor}
                        />
                      </div>
                    ) : (
                      <Avatar
                        label={getInitials(workspace.name)}
                        size={isAPMobileWeb ? 47 : 32}
                        workspaceId={workspace.id}
                        url={workspace.logo_url}
                        backgroundColor={avatarBgColor}
                      />
                    )}
                    {isAPMobileWeb ? (
                      <div className={cx(Styles.selectedWorkspaceInfo, { [Styles.newMobileWeb]: isAPMobileWeb })}>
                        <Heading level={5}>{profile?.display_name}</Heading>
                        <Text variant="label" fontWeight={'400'}>
                          {profile?.email}
                        </Text>
                      </div>
                    ) : (
                      <div className={Styles.selectedWorkspaceInfo}>
                        <span className={Styles.selectedWorkspaceName}>{profile?.display_name}</span>
                        <span className={Styles.selectedWorkspaceMembers}>{profile?.email}</span>
                      </div>
                    )}
                  </div>
                </Item>
              ),
            ].filter(Boolean) as ItemElement<HTMLElement>[]
          }
        </Section>
        {isAPMobileWeb ? null : (
          <Section>
            {
              [
                canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]) && showCreditUsage && (
                  <Item key="creditUsage" textValue="Credit Usage">
                    <CreditUsage usage={creditUsage} />
                  </Item>
                ),
                canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]) && (
                  <Item key="settings" textValue="Settings">
                    <Settings size={iconSize} /> Settings
                  </Item>
                ),
                canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]) && (
                  <Item key="billing" textValue="Your Billing">
                    <Card size={iconSize} /> Your Billing
                  </Item>
                ),
              ].filter(Boolean) as ItemElement<HTMLElement>[]
            }
          </Section>
        )}
        <Section>
          {
            [
              canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.GUEST]) && (
                <Item key="switchWorkspace" textValue="Switch Workspace">
                  <UsersProfiles1 size={iconSize} />
                  Switch Workspace
                  <div className={Styles.switchWorkspaceChevronRight}>
                    {isAPMobileWeb ? null : <ChevronRight size={16} />}
                  </div>
                </Item>
              ),
            ].filter(Boolean) as ItemElement<HTMLElement>[]
          }
        </Section>
        <Section>
          {
            [
              canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]) && !isAPMobileWeb && (
                <Item key="joinCommunity" textValue="Join Community">
                  <Users />
                  Join Our Community
                </Item>
              ),
              canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]) && !isAPMobileWeb && (
                <Item key="referAndEarn" textValue="Refer And Earn">
                  <Gift01 size={iconSize} />
                  Refer & Earn
                </Item>
              ),
              canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]) && (
                <Item key="helpCenter" textValue="Help Center">
                  <HelpCenterIcon size={iconSize} />
                  Help Center
                </Item>
              ),
              canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]) &&
                !isAPMobileWeb &&
                isAlmanacUser &&
                !isTabletPortrait && (
                  <Item key="bugReporter" textValue="Report Bug">
                    <BugIcon size={iconSize} />
                    Report Bug
                  </Item>
                ),
              canViewAs([UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]) && !isAPMobileWeb && (
                <Item key="intercomControl" textValue="Support" aria-label="Support">
                  <WithAutopilotUpgradeBadge>
                    <SupportBubble size={iconSize} />
                    Support
                  </WithAutopilotUpgradeBadge>
                </Item>
              ),
            ].filter(Boolean) as ItemElement<HTMLElement>[]
          }
        </Section>
        <Section>
          {
            [
              isAlmanacUser && showReleaseInfo && isAPMobileWeb && (
                <Item key="releaseInfo" textValue="Release Info">
                  <ArrowRefresh size={iconSize} />
                  {`Refresh (Current: ${REACT_APP_BETA_ENVIRONMENT ? 'Beta' : 'Main'})`}
                </Item>
              ),
              canOverrideFlipper && (
                <Item key="featureFlags" textValue="Feature flags">
                  <Flag size={iconSize} /> Feature flags
                </Item>
              ),
              <Item key="signout" textValue="Sign out">
                <Logout1 size={iconSize} /> Sign out
              </Item>,
            ].filter(Boolean) as ItemElement<HTMLElement>[]
          }
        </Section>
      </Menu>
    </div>
  )
}
